/**
 * Shared utility for preparing FormData for upload requests
 * Handles common form data preparation logic for both photo and CSV uploads
 */

import { FileWithPath } from '@mantine/dropzone';

export interface UploadFormFields {
  newRestaurantName: string;
  adminNotes: string;
  description: string;
  uploadMode: string;
  sourceLanguage: string;
  /** Optional. JSON string for category order (same as Edit menu). */
  categoryOrder?: string;
  /** Optional. JSON string for query setup (same as Edit menu). */
  querySetup?: string;
}

export interface RestaurantOption {
  value: string;
  label: string;
}

/**
 * Prepares FormData for upload requests with common form fields
 * @param files - Array of files to upload
 * @param fileFieldName - Field name for files ('menuImages' for photos, 'csvFile' for CSV)
 * @param selectedRestaurant - Currently selected restaurant ID
 * @param restaurants - Array of available restaurants
 * @param formFields - Form field values
 * @returns FormData object ready for upload
 */
export const prepareUploadFormData = (
  files: FileWithPath[],
  fileFieldName: string,
  selectedRestaurant: string | null,
  restaurants: RestaurantOption[],
  formFields: UploadFormFields
): FormData => {
  const formData = new FormData();
  
  // Add files with the appropriate field name
  files.forEach((file) => {
    formData.append(fileFieldName, file);
  });
  
  // Add restaurant data based on selection
  if (selectedRestaurant === 'other') {
    formData.append('name', formFields.newRestaurantName);
  } else if (selectedRestaurant) {
    // Find the selected restaurant from the list
    const selectedRestaurantData = restaurants.find(r => r.value === selectedRestaurant);
    if (selectedRestaurantData) {
      formData.append('restaurant_id', selectedRestaurant);
      formData.append('name', selectedRestaurantData.label);
    }
  }

  // Add optional form fields
  if (formFields.adminNotes) {
    formData.append('adminNotes', formFields.adminNotes);
  }

  if (formFields.description) {
    formData.append('description', formFields.description);
  }

  // Add upload mode and source language
  formData.append('uploadMode', formFields.uploadMode);
  formData.append('sourceLanguage', formFields.sourceLanguage);

  // Optional menu settings (same parameters as Edit menu Metadata tab)
  if (formFields.categoryOrder?.trim()) {
    formData.append('categoryOrder', formFields.categoryOrder.trim());
  }
  if (formFields.querySetup?.trim()) {
    formData.append('querySetup', formFields.querySetup.trim());
  }

  return formData;
};

/**
 * Validates upload form data before submission
 * @param selectedRestaurant - Currently selected restaurant
 * @param files - Array of files to upload
 * @param formFields - Form field values
 * @returns Object with validation result and error message
 */
export const validateUploadForm = (
  selectedRestaurant: string | null,
  files: FileWithPath[],
  formFields: UploadFormFields
): { isValid: boolean; error?: string } => {
  if (!selectedRestaurant) {
    return { isValid: false, error: 'Please select a restaurant first' };
  }

  if (files.length === 0) {
    return { isValid: false, error: 'Please add files to upload' };
  }

  if (selectedRestaurant === 'other' && !formFields.newRestaurantName.trim()) {
    return { isValid: false, error: 'Please enter a restaurant name' };
  }

  return { isValid: true };
};
